import React from "react";
import { Box, ButtonBase, Collapse, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import { buildHaklaiSectionChromeStyles } from "../table/haklaiTableShared";

// ── HaklaiSectionAccordion ─────────────────────────────────────────────
//
// Drop-in accordion primitive that renders the *same* header/chrome as
// `HaklaiPreCalcSection` / `HaklaiPropertyPartsSection` / `HaklaiExpensesSection`
// (ButtonBase header + space-between flex + ExpandMoreIcon sized/positioned
// via `buildHaklaiSectionChromeStyles`). Purpose: keep the accordion
// arrow perfectly aligned across every section in the Haklai flow.
//
// Why not reuse `../../layout/CustomAcordion`?
//   `CustomAcordion` wraps MUI v4's `AccordionSummary`, which has
//   `padding: 0 24px` and an `expandIcon` IconButton with `marginRight: -12`
//   (LTR baseline). In RTL that combo pushes the glyph ~24px from the
//   visual-left edge, whereas the haklai chrome puts it ~16px from the
//   visual-left edge — an ~8px horizontal mismatch that's clearly
//   visible when these sections sit next to each other. `CustomAcordion`
//   is still used by `dealMainCalc` / `justPrisaCalc`, so we can't
//   retune it without shifting those screens too. A Haklai-local
//   primitive keeps the fix scoped.
//
// API mirrors `CustomAcordion` so callers can swap with minimal noise:
//   - summaryText           (string)   — header label
//   - expanded              (bool)     — controlled-mode open state
//   - onToggleExpanded      (fn)       — called on header click (controlled)
//   - defaultExpanded       (bool)     — uncontrolled initial state
//   - children              (ReactNode) — rendered inside the open body
//
// Pattern note: just like the other haklai sections, callers that need
// a "locked + yellow banner" treatment render the banner inside
// `children` (above their main content), so it sits inside this
// component's `<Collapse>` and auto-animates with the accordion.
const useStyles = makeStyles((theme) => buildHaklaiSectionChromeStyles(theme));

const HaklaiSectionAccordion = ({
  summaryText,
  expanded,
  onToggleExpanded,
  defaultExpanded = false,
  children
}) => {
  const classes = useStyles();

  // Support both controlled (expanded + onToggleExpanded passed) and
  // uncontrolled (neither passed, falls back to defaultExpanded) modes,
  // matching `CustomAcordion`'s dual API. When uncontrolled, we track
  // open state locally so the click handler still works.
  const isControlled = typeof expanded === "boolean";
  const [internalExpanded, setInternalExpanded] = React.useState(defaultExpanded);
  const isOpen = isControlled ? expanded : internalExpanded;

  const handleToggle = () => {
    if (isControlled) {
      if (typeof onToggleExpanded === "function") onToggleExpanded();
    } else {
      setInternalExpanded((prev) => !prev);
    }
  };

  return (
    <Box className={classes.section}>
      <Box className={classes.sectionHeader}>
        <ButtonBase className={classes.sectionHeaderButton} onClick={handleToggle}>
          <Typography className={classes.sectionHeaderTitle}>{summaryText}</Typography>
          <ExpandMoreIcon
            className={[
              classes.sectionToggleIcon,
              isOpen ? classes.sectionToggleIconExpanded : ""
            ].filter(Boolean).join(" ")}
          />
        </ButtonBase>
      </Box>
      <Collapse in={isOpen} unmountOnExit={false}>
        <Box className={classes.sectionContent}>{children}</Box>
      </Collapse>
    </Box>
  );
};

export default HaklaiSectionAccordion;
